import type { Rng } from './rng';
import type { Agent, Intent, Lane, World } from './types';

/** Walking pace in feet per second: a trade-show stroll, not a commute. */
export const WALK_SPEED = { min: 3.2, max: 4.4 } as const;

/** How far past the booth an attendee walks before leaving the model. */
export const RUNOUT = 14;

/**
 * The cross aisle dead-ends at the hall perimeter just behind our back line,
 * so people on it appear and leave at the wall rather than 14' beyond it.
 */
export const PERIMETER_Z = -0.6;

/**
 * Share of each intent. Most people keep walking; a few stop for the screen;
 * fewer still want hands-on time. Tune here, not in the renderer.
 */
export const INTENT_WEIGHTS: ReadonlyArray<readonly [Intent, number]> = [
  ['pass', 55],
  ['glance', 20],
  ['watch', 15],
  ['demo', 10],
];

let nextId = 1;

/** Resets ids; tests call this so expectations are stable. */
export function resetAgentIds(): void {
  nextId = 1;
}

/**
 * Puts a new attendee at one end of an aisle. The main aisle sees roughly
 * three times the side aisle's traffic; a corner's whole point is that both
 * carry people.
 */
export function spawnAgent(rng: Rng, world: World): Agent {
  const lane: Lane = rng.pick([
    ['main', 3],
    ['side', 1],
  ]);
  const dir: 1 | -1 = rng.next() < 0.5 ? 1 : -1;
  const laneOffset = rng.range(1.2, world.aisle - 1.2);
  let x: number;
  let z: number;
  if (lane === 'main') {
    z = world.depth + laneOffset;
    x = dir === 1 ? -world.aisle - RUNOUT : world.width + RUNOUT;
  } else {
    x = world.width + laneOffset;
    z = dir === 1 ? PERIMETER_Z : world.depth + world.aisle + RUNOUT;
  }
  return {
    id: nextId++,
    x,
    z,
    heading: 0,
    intent: rng.pick(INTENT_WEIGHTS),
    state: 'walking',
    lane,
    dir,
    laneOffset,
    speed: rng.range(WALK_SPEED.min, WALK_SPEED.max),
    timer: 0,
    target: null,
    kioskId: null,
    stuck: 0,
    greeted: false,
    tint: rng.next(),
  };
}
